import { Banner } from 'component/banner';
import { Center, HorizBox, Pad, PadBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { colorSurfacePrimary, colorSurfaceSecondary, colorTextSecondary } from 'component/color';
import { DropDownSelector } from 'component/popup';
import { Heading, UtilityText } from 'component/text';
import { REPLACE_ZDF_FilterButton } from 'open-modules/zdf/component/button';
import { useModerationAuthorsByIds } from 'open-modules/zdf/component/moderation';
import { useParentDatastore } from 'open-modules/zdf/component/parentdatastore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SpacedArray } from 'system/demo';
import { useDatastore, useInstanceKey, useModuleObject, usePersonaKey, useSessionData } from 'util/datastore';
import { internal_gotoInstance } from 'util/navigate';
import { useInstanceParams } from 'util/params';
import { t } from 'util/stdtypes';
import { generateRandomKey } from 'util/util';

import { JudgementCard } from './judgementcard';
import { ModDashboardCommentCard } from './moddashboardcommentcard';
import { ModerationFilters, ModerationFiltersDefault } from './moddashboardFilters';
import {
    useFilteredQueueAllInstances,
    useFilteredQueueCurrentInstance,
    useFilteredQueues,
    useGlobalQueueCounts,
    useModQueue,
} from './moddashboardqueuehooks';
import { moduleMapBookmarks, moduleMapQueue, moduleMapSortOrder, moduleMapTaskWatchState } from './moddashboardtypes';

export function ModDashboardQueueGeneral() {
    return (
        <View style={ModDashboardQueueStyle.generalQueueOuterContainer}>
            <PadBox top={80}>
                <Heading label={t('Comments')} level={1} type='large' weight='medium' />
                <ModDashboardQueue additionalFilters={[ModerationFilters.Bookmarked]} />
            </PadBox>
        </View>
    );
}

const ModDashboardQueueStyle = StyleSheet.create({
    generalQueueOuterContainer: {
        flex: 1,
        flexShrink: 1,
        height: '100%',
    },
    queueOuterContainer: {
        flexGrow: 1,
        flexShrink: 1,
        paddingBottom: 80,
    },
    loadNewItemsButton: {
        position: 'relative',
        zIndex: 10,
        width: '100%',
    },
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ModDashboardQueue({
    structureKey,
    instanceKey,
    amountItems = 20,
    additionalFilters = [],
    useAllFilteredQueues = useFilteredQueues,
    useQueueCounts,
}) {
    const [selectedFilters, setSelectedFilters] = useState([ModerationFilters.AwaitingDecision]);

    const savedSortingOrder = useModuleObject(moduleMapSortOrder, 'queue', { once: true });
    const [sortingOrder, setSortingOrder] = useState(null);

    const datastore = useDatastore();
    const [limit, setLimit] = useState(amountItems);
    const [startTime, setStartTime] = useState(Date.now());

    const useSingleFilteredQueue = useMemo(() => {
        if (structureKey && instanceKey) {
            return useFilteredQueueCurrentInstance;
        } else {
            return useFilteredQueueAllInstances;
        }
    }, [structureKey, instanceKey]);

    const useMemoGlobalQueueCounts = useMemo(() => {
        return structureKey && instanceKey ? null : useGlobalQueueCounts;
    }, [structureKey, instanceKey]);

    const { queue, newItemsToLoad, newItemsToLoadCount, queueCounts } = useModQueue({
        filterOptions: selectedFilters,
        sortingOrder: sortingOrder ?? savedSortingOrder,
        limit,
        startTime,
        structureKey,
        instanceKey,
        useAllFilteredQueues,
        useSingleFilteredQueue,
        useQueueCounts: useQueueCounts || useMemoGlobalQueueCounts,
    });

    function toggleFilter({ filterOption }) {
        // Remove filter from list
        if (selectedFilters.includes(filterOption)) {
            setSelectedFilters(selectedFilters.filter((selectedFilter) => selectedFilter !== filterOption));
        }
        // Add filter to list
        else {
            setSelectedFilters([...selectedFilters, filterOption]);
        }

        // Reset the search parameters
        setLimit(amountItems);
        setStartTime(Date.now());
    }

    const loadNewItems = () => {
        setLimit(limit + newItemsToLoad.length);
        setStartTime(Date.now());
    };

    useEffect(() => {
        // Change selection when filters or sorting order changes
        datastore.setSessionData(['modTask-selected-idx'], null);
    }, [selectedFilters, sortingOrder]);

    const handleSortingOrderChange = (newSortingOrder) => {
        datastore.setModuleObjectAsync(moduleMapSortOrder, 'queue', newSortingOrder);
        setSortingOrder(newSortingOrder);
    };

    return (
        <View style={ModDashboardQueueStyle.queueOuterContainer}>
            <View style={{ backgroundColor: colorSurfaceSecondary }}>
                <Pad size={26} />
                <SpacedArray horiz pad={8}>
                    {ModerationFiltersDefault.concat(additionalFilters).map((filter, index) => (
                        <REPLACE_ZDF_FilterButton
                            count={queueCounts[filter.id] <= 100 ? queueCounts[filter.id].toString() : '100+'}
                            countColor={colorTextSecondary}
                            emoji={filter.emoji}
                            key={filter.label + index}
                            label={filter.label}
                            onPress={() => toggleFilter({ filterOption: filter })}
                            selected={selectedFilters.includes(filter)}
                        />
                    ))}
                </SpacedArray>
                <Pad size={26} />
                <DropDownSelector
                    onChange={handleSortingOrderChange}
                    options={[
                        { key: 'newest', label: t('Newest') },
                        { key: 'oldest', label: t('Oldest') },
                    ]}
                    testID={'sortingOrder'}
                    value={sortingOrder ?? savedSortingOrder}
                />
            </View>
            {/* Preparation for the new items banner, not rendered yet */}
            {(false && newItemsToLoad.length) > 0 && (
                <View style={ModDashboardQueueStyle.loadNewItemsButton}>
                    <View style={{ position: 'absolute', left: 0, right: 0 }}>
                        <Center>
                            <CTAButton label={t('New items available')} onPress={loadNewItems} type='secondary' />
                        </Center>
                    </View>
                </View>
            )}
            <ModDashboardQueueList queue={queue} />
            {queue.length > 0 && newItemsToLoadCount > 0 && (
                <HorizBox>
                    <View style={{ flexGrow: 1 }}>
                        <Center>
                            <Pad size={8} />
                            <CTAButton label={t('Load more')} onPress={() => setLimit(limit + amountItems)} />
                        </Center>
                    </View>
                    <PadBox horiz={150} />
                </HorizBox>
            )}
        </View>
    );
}

export function ModDashboardQueueList({ queue = [] }) {
    const modAuthors = useModerationAuthorsByIds(queue.map((modTask) => modTask.key));

    return (
        <View>
            {queue.length > 0 ? (
                <>
                    {queue.map((modTask) => (
                        <ModTask
                            key={'modTask' + modTask.key + modTask.judgement}
                            modAuthor={modAuthors?.[modTask.key]}
                            modTaskKey={modTask.key}
                        />
                    ))}
                </>
            ) : (
                <NoModTasksBanner />
            )}
        </View>
    );
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function useTaskWatchState({ modTask, selected }) {
    const expirationTime = 120000; // 2 Minutes
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const taskWatchState = useModuleObject(moduleMapTaskWatchState, modTask.key);
    const taskWatchStateRef = useRef(taskWatchState);
    const temporarySessionId = useSessionData(['moderation', 'sessionId']);

    // Check if Task Watch state is expired (2 minutes)
    // Failsafe in case a taskWatchState is not properly cleaned up (e.g. direct tab close)
    const taskWatchStateValid = taskWatchState && Date.now() - taskWatchState.time < expirationTime;
    const taskWatchStateHeartBeatRef = useRef(null);

    async function deleteFromReview() {
        if (taskWatchStateRef.current !== null && taskWatchStateRef.current !== undefined) {
            await datastore.callServerAsync('moderationZdf', 'unwatchTask', { modTaskId: modTask.key, modId: personaKey });
        }
        clearTimeout(taskWatchStateHeartBeatRef.current);
    }
    async function takeIntoReview() {
        if (!taskWatchStateRef.current || !taskWatchStateValid || taskWatchState.sessionId === temporarySessionId) {
            await datastore.callServerAsync('moderationZdf', 'watchTask', {
                modTaskId: modTask.key,
                modId: personaKey,
                sessionId: temporarySessionId,
            });
        }
    }

    useEffect(() => {
        taskWatchStateRef.current = taskWatchState;

        // Generate a new session id if not already done
        if (!temporarySessionId) {
            datastore.setSessionData(['moderation', 'sessionId'], generateRandomKey(10));
            return;
        }

        if (!taskWatchStateValid && selected) {
            takeIntoReview();
        }

        // If you hold the task watch state, assign it again at half of the expiration time
        if (taskWatchStateValid && !taskWatchStateHeartBeatRef.current && taskWatchState.sessionId === temporarySessionId && selected) {
            taskWatchStateHeartBeatRef.current = setTimeout(() => {
                takeIntoReview();
            }, [expirationTime / 2]);
        }
        // You do not select the card anymore, you have to unlock the task watch state
        if (!selected && taskWatchStateValid && taskWatchState.sessionId === temporarySessionId) {
            deleteFromReview();
        }

        return () => {
            if (taskWatchStateHeartBeatRef.current) {
                clearTimeout(taskWatchStateHeartBeatRef.current);
            }
        };
    }, [selected, taskWatchState, temporarySessionId]);

    // Release the watchstate on unmount
    useEffect(() => {
        return () => {
            if (taskWatchStateRef.current && taskWatchStateRef.current?.sessionId === temporarySessionId) {
                deleteFromReview();
            }
        };
    }, []);

    return useMemo(() => {
        return taskWatchStateValid
            ? {
                  ...taskWatchState,
                  amIViewing: taskWatchState.sessionId === temporarySessionId,
              }
            : null;
    }, [taskWatchState]);
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ModDashboardQueueListItem({ modTask, modAuthor }) {
    const parentDatastore = useParentDatastore();
    // Selected id has to be taken from the top level session store to make closing possible in case of embedded instances
    const selectedIdx = parentDatastore.getSessionData(['modTask-selected-idx']);
    const urlParams = useInstanceParams();
    const isOnThreadsPage = urlParams?.pageId === 'page-threads' && !urlParams.userId;
    const isInTreeView = urlParams?.modTaskTreeKey && !urlParams.userId;
    const isSelected = selectedIdx === modTask.key;

    const taskWatchState = useTaskWatchState({ modTask: modTask, selected: isSelected });
    const instanceKey = useInstanceKey();
    const isBookmarked = useModuleObject(moduleMapBookmarks, modTask.key)?.isBookmarked || false;

    const [isReduced, setIsReduced] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= window.screen.availWidth / 1.3) {
                setIsReduced(true);
            } else {
                setIsReduced(false);
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const onPressView = () => {
        // Go to mod task tree view
        const { userId, ...rest } = urlParams ?? {};
        const newParams = {
            ...rest,
            pageId: 'page-threads',
            threadId: modTask.instanceKey,
            modTaskTreeKey: modTask.key,
        };
        parentDatastore.gotoInstance({ structureKey: 'moddashboard', instanceKey, params: newParams });
    };

    const onExpandCard = () => {
        parentDatastore.setSessionData(['modTask-selected-idx'], modTask.key);
    };

    const onCollapseCard = () => {
        parentDatastore.setSessionData(['modTask-selected-idx'], null);
    };

    const onClick = (e) => {
        // Mark click event as from modTask an propagate it
        e.isFromModTask = true;
        return false;
    };

    return (
        <View>
            <Pad size={16} />
            <HorizBox>
                <ModDashboardCommentCard
                    highlighted={isSelected}
                    isBookmarked={isBookmarked}
                    isViewDisabled={isInTreeView}
                    modAuthor={modAuthor}
                    modTask={modTask}
                    onCollapse={onCollapseCard}
                    onExpand={onExpandCard}
                    onPressView={onPressView}
                    viewLabel={isOnThreadsPage ? 'View' : 'View in thread'}
                />
                {/* Judgement card reserves a fixed amout of space to not make the comment card change in width */}
                <View style={!isReduced ? { width: 300 } : { width: 250 }}>
                    {
                        <HorizBox>
                            <Pad size={16} />
                            <View onClick={onClick} style={{ maxWidth: '100%' }}>
                                <JudgementCard
                                    isSelected={isSelected}
                                    isWarnDisabled={!!modTask.deleted}
                                    modAuthor={modAuthor}
                                    modTask={modTask}
                                    onCollapse={onCollapseCard}
                                    taskWatchState={taskWatchState}
                                />
                            </View>
                        </HorizBox>
                    }
                </View>
            </HorizBox>
        </View>
    );
}

export function NoModTasksBanner() {
    return (
        <View style={{ width: 'fit-content' }}>
            <Pad size={16} />
            <Banner color={colorSurfacePrimary}>
                <UtilityText label={t('No comments')} />
            </Banner>
        </View>
    );
}

function ModTask({ modTaskKey = '', modAuthor }) {
    //Subscribe to main queue item
    const modTask = useModuleObject(moduleMapQueue, modTaskKey);

    return <View>{modTask?.type === 'comment' && <ModDashboardQueueListItem modAuthor={modAuthor} modTask={modTask} />}</View>;
}
